import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Stack, Button, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";

const B = {
  navy:     "#00357a",
  navyDark: "#001f4d",
  navyMid:  "#004299",
  gold:     "#c9a96e",
  goldLight:"#dfc08a",
};

const DOT_GRID_LIGHT = "radial-gradient(circle, rgba(0,53,122,0.09) 1.2px, transparent 1.2px)";
const DOT_GRID_DARK  = "radial-gradient(circle, rgba(255,255,255,0.045) 1.2px, transparent 1.2px)";

const TRUST = ["CAP Accredited", "HIPAA Compliant", "GCP BAA Signed", "CLIA Certified"];

const CARDS = [
  {
    // P4 — merged: was two separate cards (patient, clinician) pointing at
    // two separate 700-line components. One form now generates both
    // flavors together in one step (Report_Generation.js) — one card.
    id: "generate",
    path: "/generate_report",
    badge: "GENERATE",
    title: "Generate Report",
    tagline: "One upload, both the patient and clinician report",
    description:
      "Upload a lab PDF once — generates the plain-language patient report AND the evidence-based clinician report together, with lifestyle guidance, risk indicators, and citations, delivered in one step.",
    accentFrom: "#3b82f6",
    accentTo:   "#10b981",
    features: [
      { icon: "📋", text: "AI-extracted test results & ranges" },
      { icon: "💬", text: "Plain-language patient summaries" },
      { icon: "⚡", text: "Clinician risk scoring & citations" },
      { icon: "📥", text: "Both branded PDFs, one delivery" },
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
        <circle cx="16" cy="16" r="7" stroke="currentColor" strokeWidth="2.5" />
        <path d="M5 40c0-7.18 4.925-13.2 11.5-14.65" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <rect x="26" y="8" width="18" height="28" rx="3" stroke="currentColor" strokeWidth="2.5" />
        <path d="M31 17h8M31 23h8M31 29h5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: "reports",
    path: "/reports",
    badge: "DASHBOARD",
    title: "Report Storage",
    tagline: "Manage every generated report in one place",
    description:
      "Search, filter, download, and delete all reports stored in GCS — organised by lab, date, and report type with instant signed-URL access. Your diagnostic history, always available.",
    accentFrom: "#f59e0b",
    accentTo:   "#ef4444",
    features: [
      { icon: "🗂️", text: "Organised by lab, date & type" },
      { icon: "🔍", text: "Full-text search & smart filters" },
      { icon: "⬇️", text: "Instant GCS signed-URL downloads" },
      { icon: "🗑️", text: "Archive & delete old reports" },
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
        <path d="M8 12a4 4 0 014-4h6l4 4h14a4 4 0 014 4v20a4 4 0 01-4 4H12a4 4 0 01-4-4V12z"
          stroke="currentColor" strokeWidth="2.5" />
        <path d="M24 22v8M21 27l3 3 3-3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "requisition_conversion",
    path: "/requisition_conversion",
    badge: "TOOLS",
    title: "Requisition Conversion",
    tagline: "Convert third-party requisitions into Labarium's own form",
    description:
      "Upload a partner lab's handwritten requisition, extract it via OCR, review and correct on a Labarium-formatted web replica, then save and download a merged, lab-ready PDF.",
    accentFrom: "#8b5cf6",
    accentTo:   "#6d28d9",
    features: [
      { icon: "📝", text: "Handwriting-tuned OCR extraction" },
      { icon: "🧾", text: "Auto-fills a Labarium-formatted replica" },
      { icon: "✅", text: "Review, correct, and save" },
      { icon: "🔎", text: "Look up and re-download past conversions" },
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
        <path d="M14 6h14l6 6v30a2 2 0 01-2 2H14a2 2 0 01-2-2V8a2 2 0 012-2z"
          stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M28 6v6h6" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M18 26l4 4 8-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    // Lab Orders — staff-facing, regular `token` auth (NOT admin-gated,
    // unlike the removed Audit Log card below), server-side scoped to
    // the logged-in staff user's own lab. Same Lab Orders feature as the
    // admin dashboard's cross-lab version, just pre-filtered to one lab.
    id: "lab_orders",
    path: "/lab-orders",
    badge: "ORDERS",
    title: "Lab Orders",
    tagline: "Browse every order submitted for your lab",
    description:
      "Clinician → Patient → Order, filterable by clinician NPI, patient, test type, date range, status, and more — click through to a full digital requisition, or download the original signed PDF.",
    accentFrom: "#0ea5e9",
    accentTo:   "#0369a1",
    features: [
      { icon: "🌳", text: "Clinician → Patient → Order tree" },
      { icon: "🔎", text: "Filter by NPI, test type, status, date" },
      { icon: "📄", text: "Full digital requisition, not just a PDF" },
      { icon: "⬇️", text: "Original signed PDF still one click away" },
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
        <path d="M10 10h12M10 18h20M10 26h20M10 34h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="36" cy="34" r="6" stroke="currentColor" strokeWidth="2.5" />
      </svg>
    ),
  },
  // 2026-10-04 — removed: "Patient Summaries" (P7) and "Audit Log" (P10)
  // cards used to live here. Two real problems with that: (1) this is the
  // staff-facing report-GENERATION app — per explicit instruction, patient
  // summaries belong only on the patient/clinician-facing labariumaiportal
  // side, not here; (2) "Audit Log" linked to /admin/audit-log, which is
  // gated on a separate admin_token every regular staff user viewing this
  // page lacks — clicking it just bounced them to /admin-login, confusing
  // UX for a card sitting on the everyday staff landing page. Audit Log is
  // now linked from inside AdminDashboard.js instead, where it actually
  // belongs. Removing these two also fixes the reported "cards are super
  // thin" layout complaint — this grid was designed for 3 cards, not 5.
];

function RoleCard({ card, isDark, onClick }) {
  const [hovered, setHovered] = useState(false);
  const bg = isDark ? "rgba(17,24,39,0.9)" : "rgba(255,255,255,0.95)";
  const textSub = isDark ? "#94a3b8" : "#475569";

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      whileHover={{ y: -10, scale: 1.01 }}
      whileTap={{ scale: 0.975 }}
      transition={{ type: "spring", stiffness: 380, damping: 26 }}
      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
    >
      <Box sx={{
        height: "100%", display: "flex", flexDirection: "column",
        borderRadius: "24px", overflow: "hidden", position: "relative",
        background: isDark
          ? "linear-gradient(rgba(17,24,39,0.92), rgba(17,24,39,0.92)) padding-box, linear-gradient(135deg, rgba(0,53,122,0.6), rgba(201,169,110,0.4)) border-box"
          : "linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95)) padding-box, linear-gradient(135deg, rgba(0,53,122,0.2), rgba(201,169,110,0.35)) border-box",
        border: "1.5px solid transparent",
        backdropFilter: "blur(12px)",
        boxShadow: hovered
          ? isDark
            ? "0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,169,110,0.3), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 32px 80px rgba(0,31,77,0.2), 0 0 0 1px rgba(201,169,110,0.35), inset 0 1px 0 rgba(255,255,255,0.9)"
          : isDark
            ? "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "0 4px 28px rgba(0,31,77,0.09), inset 0 1px 0 rgba(255,255,255,1)",
        transition: "box-shadow 0.3s ease",
      }}>
        {/* Gradient accent bar */}
        <Box sx={{
          height: 3,
          background: `linear-gradient(to right, ${card.accentFrom}, ${card.accentTo}, ${B.gold})`,
          opacity: hovered ? 1 : 0.65,
          transition: "opacity 0.3s",
          flexShrink: 0,
        }} />

        {/* Header */}
        <Box sx={{
          px: 3.5, pt: 3.5, pb: 3, position: "relative", overflow: "hidden", flexShrink: 0,
          background: `linear-gradient(135deg, ${B.navyDark} 0%, ${B.navyMid} 100%)`,
        }}>
          <Box sx={{ position: "absolute", top: -60, right: -60, width: 180, height: 180,
            borderRadius: "50%", border: "1px solid rgba(201,169,110,0.1)" }} />
          <Box sx={{ position: "absolute", top: -36, right: -36, width: 110, height: 110,
            borderRadius: "50%", border: "1px solid rgba(201,169,110,0.16)" }} />

          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            bgcolor: "rgba(201,169,110,0.15)", border: "1px solid rgba(201,169,110,0.25)",
            borderRadius: "99px", px: 1.5, py: 0.4, mb: 2.5,
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: B.gold,
              "@keyframes pulse2": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
              animation: "pulse2 2s infinite" }} />
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: B.gold, letterSpacing: "0.15em", fontFamily: "'Inter', sans-serif" }}>
              {card.badge}
            </Typography>
          </Box>

          <motion.div animate={{ rotate: hovered ? 8 : 0 }} transition={{ duration: 0.3 }}>
            <Box sx={{ color: B.gold, mb: 1.5 }}>{card.icon}</Box>
          </motion.div>

          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#fff", lineHeight: 1.2, mb: 0.6,
            fontFamily: "'Playfair Display', serif" }}>
            {card.title}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.42)", lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>
            {card.tagline}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3.5, py: 3, flex: 1, display: "flex", flexDirection: "column", bgcolor: bg }}>
          <Typography sx={{ fontSize: 15, color: textSub, lineHeight: 1.9, mb: 3, fontFamily: "'Inter', sans-serif" }}>
            {card.description}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
            {card.features.map((f, i) => (
              <motion.div key={i}
                animate={{ x: hovered ? 5 : 0, opacity: hovered ? 1 : 0.9 }}
                transition={{ delay: hovered ? i * 0.04 : 0, duration: 0.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "8px", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,122,0.05)", fontSize: 14,
                  }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: 14.5, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                    {f.text}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          <Box sx={{ mt: 3.5 }}>
            <Button fullWidth variant="contained"
              endIcon={
                <motion.div animate={{ x: hovered ? 5 : 0 }} transition={{ duration: 0.2 }}>
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </motion.div>
              }
              sx={{
                bgcolor: B.navy, color: "#fff", fontWeight: 700, fontSize: 15,
                py: 1.5, borderRadius: "12px", letterSpacing: "0.02em", textTransform: "none",
                fontFamily: "'Inter', sans-serif",
                boxShadow: hovered ? "0 10px 28px rgba(0,31,77,0.45)" : "0 2px 10px rgba(0,31,77,0.2)",
                transition: "all 0.25s ease",
                background: hovered ? `linear-gradient(135deg, ${B.navyDark}, ${B.navyMid})` : B.navy,
                "&:hover": { background: `linear-gradient(135deg, ${B.navyDark}, ${B.navyMid})` },
              }}>
              Open {card.badge.charAt(0) + card.badge.slice(1).toLowerCase()}
            </Button>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const firstName = user?.full_name?.split(" ")[0] || "there";
  const bg = isDark ? "#080d1a" : "#f4f7ff";

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Dot-grid pattern */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        backgroundImage: isDark ? DOT_GRID_DARK : DOT_GRID_LIGHT,
        backgroundSize: "28px 28px", opacity: 0.9,
      }} />

      {/* Ambient blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-8%",  left: "-6%",  size: 560, color: isDark ? "rgba(0,53,122,0.22)" : "rgba(0,53,122,0.07)" },
          { top: "55%",  right: "-6%", size: 420, color: isDark ? "rgba(201,169,110,0.09)" : "rgba(201,169,110,0.13)" },
          { top: "20%",  left: "55%",  size: 340, color: isDark ? "rgba(0,31,77,0.2)" : "rgba(59,130,246,0.05)" },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(100px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 10 + i * 3, repeat: 2, delay: i * 2.5, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* ── Navbar ── */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        style={{ position: "relative", zIndex: 20, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(201,169,110,0.2)",
          boxShadow: "0 4px 28px rgba(0,31,77,0.2)",
        }}>
          {/* Gold accent line — top */}
          <Box sx={{
            height: 2,
            background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)",
          }} />
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <ThemeToggle />
              <NavUserMenu />
            </Stack>
          </Box>
        </Box>
      </motion.div>

      {/* ── Hero ── */}
      <Box sx={{ position: "relative", zIndex: 10, textAlign: "center",
        pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 }, px: 3 }}>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 600, letterSpacing: "0.25em",
            color: isDark ? "#64748b" : "#8896a8", mb: 2, textTransform: "uppercase",
            fontFamily: "'Inter', sans-serif",
          }}>
            {new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening"}, {firstName}
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.18 }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 1,
            bgcolor: isDark ? "rgba(201,169,110,0.1)" : "rgba(0,53,122,0.07)",
            border: `1px solid ${isDark ? "rgba(201,169,110,0.2)" : "rgba(0,53,122,0.12)"}`,
            borderRadius: "99px", px: 2.5, py: 0.75, mb: 3.5,
          }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: B.gold,
              "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
              animation: "pulse 2s infinite" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: B.gold, letterSpacing: "0.16em", fontFamily: "'Inter', sans-serif" }}>
              LABARIUM INTELLIGENCE — ACTIVE
            </Typography>
          </Box>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}>
          <Typography sx={{
            fontFamily: "'Playfair Display', serif",
            fontSize: { xs: 30, sm: 42, md: 52 }, fontWeight: 800,
            color: isDark ? "#f1f5f9" : B.navyDark,
            lineHeight: 1.1, mb: 2, maxWidth: 680, mx: "auto",
            letterSpacing: "-0.02em",
          }}>
            Diagnostic Intelligence,{" "}
            <Box component="span" sx={{
              background: `linear-gradient(135deg, ${B.gold} 10%, ${B.goldLight} 60%, ${B.gold} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              at your fingertips.
            </Box>
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.3 }}>
          <Typography sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 15.5, color: isDark ? "#64748b" : "#8896a8",
            mb: 4, maxWidth: 520, mx: "auto", lineHeight: 1.75, fontWeight: 400,
          }}>
            Laboratory test results inform 70% of all clinical decisions. Labarium Intelligence transforms raw lab data into actionable insights — for clinicians and patients alike.
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.38 }}>
          <Stack direction="row" justifyContent="center" flexWrap="wrap" sx={{ gap: "8px !important" }}>
            {TRUST.map((t) => (
              <Chip key={t}
                icon={<VerifiedIcon sx={{ fontSize: "12px !important", color: `${B.gold} !important` }} />}
                label={t} size="small"
                sx={{
                  fontSize: 13, fontWeight: 600, height: 28,
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
                  color: isDark ? "#94a3b8" : "#475569",
                  fontFamily: "'Inter', sans-serif",
                  "& .MuiChip-icon": { ml: 1 },
                }} />
            ))}
          </Stack>
        </motion.div>
      </Box>

      {/* ── Cards ── */}
      <Box sx={{
        flex: 1, position: "relative", zIndex: 10,
        maxWidth: 1100, mx: "auto", width: "100%",
        px: { xs: 2, sm: 3 }, pb: 8,
        display: "flex", gap: { xs: 3, md: 3.5 },
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
      }}>
        {CARDS.map((card, i) => (
          <motion.div key={card.id}
            initial={{ opacity: 0, y: 52 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 + i * 0.15, ease: [0.34, 1.56, 0.64, 1] }}
            style={{ flex: 1, display: "flex" }}>
            <RoleCard card={card} isDark={isDark} onClick={() => navigate(card.path)} />
          </motion.div>
        ))}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.92)" : B.navyDark,
        backdropFilter: "blur(10px)",
        py: 2.5, px: 3, position: "relative", zIndex: 10,
        borderTop: "1px solid rgba(201,169,110,0.12)", flexShrink: 0,
      }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", alignItems: "center",
          justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.22)", letterSpacing: "0.05em", fontFamily: "'Inter', sans-serif" }}>
            © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. ALL RIGHTS RESERVED.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.18)", fontFamily: "'Inter', sans-serif" }}>
            Better information. Better decisions. Better lives.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
